/**
 * PremiumPhoneInput.jsx
 * Input de Telefone Brasileiro com formatação automática
 *
 * Formata automaticamente:
 * - Celular: (11) 99999-9999 (11 dígitos)
 * - Fixo: (11) 3333-4444 (10 dígitos)
 *
 * Valida:
 * - DDD válido (11-99)
 * - Prefixo de celular (9xxxx)
 */

import React, { useState, useCallback, forwardRef } from 'react';
import { InputAdornment } from '@mui/material';
import PhoneIcon from '@mui/icons-material/Phone';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import PremiumInput from './PremiumInput';

/**
 * Formata telefone brasileiro
 * - Celular: (11) 99999-9999
 * - Fixo: (11) 3333-4444
 */
const formatPhone = (value) => {
  if (!value) return '';

  const clean = value.replace(/\D/g, '').slice(0, 11);

  if (clean.length === 0) return '';
  if (clean.length <= 2) return `(${clean}`;
  if (clean.length <= 6) return `(${clean.slice(0, 2)}) ${clean.slice(2)}`;

  // Celular (11 dígitos): (11) 99999-9999
  if (clean.length === 11) {
    return `(${clean.slice(0, 2)}) ${clean.slice(2, 7)}-${clean.slice(7)}`;
  }

  // Fixo (10 dígitos): (11) 3333-4444
  if (clean.length <= 10) {
    if (clean.length <= 6) {
      return `(${clean.slice(0, 2)}) ${clean.slice(2)}`;
    }
    return `(${clean.slice(0, 2)}) ${clean.slice(2, 6)}-${clean.slice(6)}`;
  }

  return `(${clean.slice(0, 2)}) ${clean.slice(2, 7)}-${clean.slice(7)}`;
};

/**
 * Valida telefone brasileiro
 */
const validatePhone = (value) => {
  if (!value) return { valid: null, message: '' };

  const clean = value.replace(/\D/g, '');

  if (clean.length === 0) {
    return { valid: null, message: '' };
  }

  // Validar DDD (11-99)
  if (clean.length >= 2) {
    const ddd = parseInt(clean.slice(0, 2), 10);
    if (ddd < 11 || ddd > 99) {
      return { valid: false, message: 'DDD inválido' };
    }
  }

  // Ainda digitando
  if (clean.length < 10) {
    return { valid: null, message: `${clean.length}/10-11 dígitos` };
  }

  // Telefone fixo (10 dígitos)
  if (clean.length === 10) {
    // Fixo não pode começar com 9 após o DDD
    if (clean[2] === '9') {
      return { valid: false, message: 'Fixo não começa com 9' };
    }
    return { valid: true, message: 'Telefone fixo válido' };
  }

  // Celular (11 dígitos)
  if (clean.length === 11) {
    // Celular deve começar com 9 após o DDD
    if (clean[2] !== '9') {
      return { valid: false, message: 'Celular deve começar com 9' };
    }
    return { valid: true, message: 'Celular válido' };
  }

  return { valid: false, message: 'Telefone inválido' };
};

/**
 * PremiumPhoneInput Component
 */
const PremiumPhoneInput = forwardRef(({
  value,
  onChange,
  label = 'Telefone',
  showIcon = true,
  ...props
}, ref) => {
  const [validation, setValidation] = useState({ valid: null, message: '' });

  const handleChange = useCallback((e) => {
    const formatted = formatPhone(e.target.value);
    const result = validatePhone(formatted);
    setValidation(result);
    onChange?.({ target: { value: formatted, name: e.target.name } });
  }, [onChange]);

  const getHelperTextColor = () => {
    if (validation.valid === true) return 'success.main';
    if (validation.valid === false) return 'error.main';
    return 'text.secondary';
  };

  return (
    <PremiumInput
      ref={ref}
      {...props}
      value={value || ''}
      onChange={handleChange}
      label={label}
      placeholder="(11) 99999-9999"
      type="tel"
      inputProps={{
        maxLength: 16,
        inputMode: 'tel',
        ...props.inputProps,
      }}
      InputProps={{
        startAdornment: showIcon ? (
          <InputAdornment position="start">
            <PhoneIcon sx={{ color: 'text.secondary', fontSize: 20 }} />
          </InputAdornment>
        ) : undefined,
        endAdornment: validation.valid === true ? (
          <InputAdornment position="end">
            <CheckCircleIcon sx={{ color: 'success.main', fontSize: 18 }} />
          </InputAdornment>
        ) : undefined,
        ...props.InputProps,
      }}
      helperText={validation.message}
      FormHelperTextProps={{
        sx: { color: getHelperTextColor() },
      }}
      error={validation.valid === false}
      success={validation.valid === true}
    />
  );
});

PremiumPhoneInput.displayName = 'PremiumPhoneInput';

export default PremiumPhoneInput;
